import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import {
  TicketsSlaPolicyDrawerService,
  TicketsSlaTargetDrawerService
} from '../../../services/tickets-config-drawer.services';
import {
  TicketPriority,
  TicketSlaPolicy,
  TicketSlaTarget
} from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-sla-policies-detail',
  standalone: true,
  imports: [CommonModule, PageBreadcrumbComponent],
  templateUrl: './sla-policies-detail.component.html'
})
export class TicketsSlaPoliciesDetailComponent implements OnInit, OnDestroy {
  policy: TicketSlaPolicy | null = null;
  targets: TicketSlaTarget[] = [];
  priorities: TicketPriority[] = [];
  loading = false;
  error = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private policyDrawer: TicketsSlaPolicyDrawerService,
    private targetDrawer: TicketsSlaTargetDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) return;
    this.cargar(id);

    this.policyDrawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      if (p.id === this.policy?.id) this.policy = p;
    });
    this.targetDrawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargarTargets());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      policy: this.configApi.getSlaPolicyById(id),
      targets: this.configApi.getSlaTargets(id).pipe(catchError(() => of([] as TicketSlaTarget[]))),
      priorities: this.configApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[])))
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ policy, targets, priorities }) => {
        this.policy = policy;
        this.priorities = priorities;
        this.targets = this.sortByPriority(targets);
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la política';
        this.loading = false;
      }
    });
  }

  private cargarTargets(): void {
    if (!this.policy?.id) return;
    this.configApi.getSlaTargets(this.policy.id)
      .pipe(catchError(() => of([] as TicketSlaTarget[])), takeUntil(this.destroy$))
      .subscribe((t) => { this.targets = this.sortByPriority(t); });
  }

  private sortByPriority(targets: TicketSlaTarget[]): TicketSlaTarget[] {
    const levelById = new Map<number, number>();
    this.priorities.forEach((p) => levelById.set(p.id, p.level));
    return [...targets].sort((a, b) => {
      const la = levelById.get(a.idTicketPriority) ?? 99;
      const lb = levelById.get(b.idTicketPriority) ?? 99;
      return la - lb;
    });
  }

  editarPolicy(): void {
    if (this.policy) this.policyDrawer.open({ id: this.policy.id });
  }

  nuevoTarget(): void {
    if (this.policy) this.targetDrawer.open({ parentId: this.policy.id });
  }

  editarTarget(t: TicketSlaTarget): void {
    if (this.policy) this.targetDrawer.open({ id: t.id, parentId: this.policy.id });
  }

  async eliminarTarget(t: TicketSlaTarget): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar objetivo SLA',
      message: `¿Eliminar el objetivo para la prioridad "${this.getPriorityLabel(t.idTicketPriority)}"?`
    });
    if (!ok) return;
    this.configApi.deleteSlaTarget(t.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Objetivo SLA'); this.cargarTargets(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar' })
    });
  }

  volver(): void { this.router.navigate(['/tickets/config/sla-policies']); }

  /** Nombre de la prioridad; si el catálogo aún no cargó, texto legible (nunca el id). */
  getPriorityLabel(idPriority: number): string {
    return this.priorities.find((p) => p.id === idPriority)?.label || 'Prioridad sin nombre';
  }

  formatMinutes(min: number): string {
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
}
